import { Edges } from 'types';
import { PlanFrequencyEnum } from '@landr/core.models';
import {
    CountryCurrency,
    findPriceForCountryCurrency,
    addTrailingSlash,
    getDecodedPathname,
    isDecodedPathnameContainsUID,
} from 'helpers';
import { DistributionPlanCodesEnum } from '../../../../enum';
import { PricingTabDocument, ApiPlan, ApiPlanFrequency, Price } from '../types';
import { Tabs } from '../components/PricingTabs';

export const getTabDataByUID = (all: Edges<PricingTabDocument>, uid: string): PricingTabDocument | null => {
    if (!all || !all.edges || !all.edges.length) {
        return null;
    }

    const edge = all.edges.find(edge => edge.node._meta.uid === uid);
    return edge ? edge.node : null;
};

/**
 * Returns the plan frequency of the specified plan
 * @param {ApiPlan} apiPlan - The API plan to search
 * @param {Frequency} frequencyName - The name of the desired frequency (Yearly or Monthly)
 */
export const getPlanFrequency = (apiPlan: ApiPlan, frequencyName: PlanFrequencyEnum): ApiPlanFrequency => {
    const fallback = {} as ApiPlanFrequency;

    if (!apiPlan || !apiPlan.frequencies) {
        return fallback;
    }

    const currentFrequency = apiPlan.frequencies.find(f => f.interval === frequencyName);

    return currentFrequency || fallback;
};

export const getPlanFrequencyLength = (apiPlan: ApiPlan) => {
    if (!apiPlan || !apiPlan.frequencies) {
        return 0;
    }

    return apiPlan.frequencies.length;
};

/**
 * Returns the price of the specified plan
 * @param {ApiPlan} apiPlan - The API plan to search
 * @param {PlanFrequencyEnum} frequencyName - The name of the desired frequency (Yearly or Monthly)
 * @param {CountryCurrency} countryCurrency - The price's currency code
 */
export const getPlanPrice = (
    apiPlan: ApiPlan,
    frequencyName: PlanFrequencyEnum,
    countryCurrency: CountryCurrency,
): Price => {
    const fallback = {} as Price;
    const frequency = getPlanFrequency(apiPlan, frequencyName);

    if (!frequency || !frequency.prices) {
        return fallback;
    }

    const price = findPriceForCountryCurrency(frequency.prices, countryCurrency);
    return price || fallback;
};

/**
 * Returns the price of a product for the specified plan
 * @param {ApiPlanFrequency} apiPlanFrequency - The API plan frequency to search
 * @param {string} code - The code of the desired product
 * @param {CountryCurrency} countryCurrency - The price's currency code
 */
export const getProductPrice = (
    apiPlanFrequency: ApiPlanFrequency,
    code: string,
    countryCurrency: CountryCurrency,
): Price => {
    const fallback = {} as Price;

    if (apiPlanFrequency && apiPlanFrequency.planProducts) {
        const product = apiPlanFrequency.planProducts.find(p => p.code === code);

        if (product && product.quantityRanges) {
            // Returns the last range of tiered pricing. We assume there is only 1 or 2 prices:
            // When there is 2, the first quantityRange is free, the second has a price.
            // If we have more than 2, we'll need to update our pricing page UX to reflect that.
            const range = product.quantityRanges.slice(-1).pop();

            if (range && range.prices) {
                const price = findPriceForCountryCurrency(range.prices, countryCurrency);
                return price || fallback;
            }
        }
    }

    return fallback;
};

/**
 * Temp functions
 *
 * Since our current pricing distro tab's uid is 'release', we could not duplicate the uid in Prismic
 * for the new pricing tab since we would have conflics displaying the new data.
 *
 * So, 'v2' was added in the new distro pricing tab urls, but since the urls to access the
 * distro pricing is "release" (in English, for example), a temporary "v2" was defined to ensure the data is displayed correctly.
 */
// TODO: remove once we get rid of prismic
export const temporaryHandleV2Tabs = (tab: string) => {
    return tab ? tab.replace('v2', '') : tab;
};

export const isLocationContainsTabsUID = (location: Location, tabs: Tabs): boolean => {
    const decodedPathname = addTrailingSlash(getDecodedPathname(location.pathname));
    // if at least one item uid is found in decodedPathname
    return tabs.some(tabItem => {
        // TODO DIS-198 remove this when we can deprecate the old pricing tabs in prismic
        let tabUid = temporaryHandleV2Tabs(tabItem.tab._meta.uid);
        return isDecodedPathnameContainsUID(decodedPathname, tabUid);
    });
};

export const isPricingTabOfTypeDistribution = (pricingTabData: PricingTabDocument): boolean => {
    return pricingTabData.plans.some(({ plan }) => {
        return Object.keys(DistributionPlanCodesEnum).includes(plan.api_plan.code);
    });
};
